import type { RangeKey } from '@dataTypes/dashboard.types'
import type { DashboardMetricItemData } from '../types/dashboardMetrics.types'
import type {
  SameDayCancellationChartConfig,
  SchedulingOverviewResult,
} from '@services/api/dashboard/scheduling.api'
import { formatCurrency, formatPercent } from '@utils/dashboardFinance.util'

export interface OverviewSameDayLineChartConfig {
  data: Array<{ label: string; value: number }>
  yDomain: [number, number]
  yTicks: number[]
}

function formatDurationMinutes(totalMinutes: number): string {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return '0 mins'
  if (totalMinutes < 60) return `${Math.round(totalMinutes)} mins`
  const hours = totalMinutes / 60
  if (hours < 10) {
    const rounded = Math.round(hours * 10) / 10
    return `${rounded} ${rounded === 1 ? 'hr' : 'hrs'}`
  }
  return `${Math.round(hours).toLocaleString('en-US')} hrs`
}

export function parseDurationGoalMinutes(
  goal: string | number | undefined,
): number {
  if (goal == null || goal === '') return 0
  if (typeof goal === 'number') return Number.isFinite(goal) ? goal : 0
  const normalized = String(goal).trim().toLowerCase()
  const n = parseFloat(normalized.replace(/[^0-9.]/g, ''))
  if (!Number.isFinite(n)) return 0
  if (/\bhours?\b|\bhrs?\b/.test(normalized)) return Math.round(n * 60)
  return Math.round(n)
}

function goalForMetric(
  mockMetrics: DashboardMetricItemData[],
  id: string,
): string | number | undefined {
  return mockMetrics.find((m) => m.id === id)?.valueOver
}

function parsePercentGoal(goal: string | number | undefined): number {
  if (goal == null) return 0
  const n = parseInt(String(goal).replace(/[^\d]/g, ''), 10)
  return Number.isFinite(n) ? n : 0
}

export function mapSchedulingOverviewToPrimaryMetrics(
  overview: SchedulingOverviewResult,
  range: RangeKey,
  mockMetrics: DashboardMetricItemData[],
): DashboardMetricItemData[] {
  const { summary } = overview
  const noShowRate = summary.noShowRate.current
  const openChairMinutes = summary.openChairMinutes.current
  const noShowGoal = parsePercentGoal(goalForMetric(mockMetrics, 'no-show'))
  return [
    {
      id: 'scheduled-today',
      leftLabel: range === '1D' ? 'Scheduled today' : 'Scheduled',
      value: Math.round(summary.scheduled.current).toLocaleString('en-US'),
      valueOver: goalForMetric(mockMetrics, 'scheduled-today'),
      infoTooltip:
        'Number of appointments scheduled compared with the daily target.',
      showInfoIcon: true,
      showWarningIcon:
        Math.round(summary.scheduled.current) <
        Math.round(
          parsePercentGoal(goalForMetric(mockMetrics, 'scheduled-today')),
        ),
    },
    {
      id: 'confirmation-rate',
      leftLabel: 'Confirmation rate',
      value: formatPercent(summary.confirmationRate.current, 0),
      valueOver: goalForMetric(mockMetrics, 'confirmation-rate'),
      showInfoIcon: true,
      infoTooltip:
        'Percentage of appointments confirmed compared with the target.',
      showWarningIcon:
        Math.round(summary.confirmationRate.current) <
        Math.round(
          parsePercentGoal(goalForMetric(mockMetrics, 'confirmation-rate')),
        ),
    },
    {
      id: 'open-chair-time',
      leftLabel: 'Open chair time',
      value: formatDurationMinutes(openChairMinutes),
      valueOver: goalForMetric(mockMetrics, 'open-chair-time'),
      showInfoIcon: true,
      infoTooltip: 'Total time spent on open chairs compared with the target.',
      showWarningIcon:
        Math.round(openChairMinutes) >
        parseDurationGoalMinutes(goalForMetric(mockMetrics, 'open-chair-time')),
    },
    {
      id: 'no-show',
      leftLabel: 'No-show',
      value: formatPercent(noShowRate, 0),
      valueOver: goalForMetric(mockMetrics, 'no-show'),
      infoTooltip:
        'Percentage of appointments marked as no-show compared with the target.',
      showInfoIcon: true,
      valueTone: noShowRate > noShowGoal ? 'danger' : undefined,
      showWarningIcon:
        Math.round(noShowRate) >
        Math.round(parsePercentGoal(goalForMetric(mockMetrics, 'no-show'))),
    },
  ]
}

export function mapSchedulingOverviewToSecondaryMetrics(
  overview: SchedulingOverviewResult,
  mockSecondaryMetrics: DashboardMetricItemData[],
): DashboardMetricItemData[] {
  const { summary } = overview
  const newPatientCount = Math.round(summary.newPatients.current)
  const pendingValue = summary.pendingTreatmentValue.current
  return mockSecondaryMetrics.map((metric) => {
    switch (metric.id) {
      case 'hygiene-util':
        return {
          ...metric,
          value: formatPercent(summary.hygieneUtilizationRate.current, 0),
          valueOver: goalForMetric(mockSecondaryMetrics, 'hygiene-util'),
          showWarningIcon:
            Math.round(summary.hygieneUtilizationRate.current) <
            Math.round(
              parsePercentGoal(
                goalForMetric(mockSecondaryMetrics, 'hygiene-util'),
              ),
            ),
        }
      case 'same-day-tx':
        return {
          ...metric,
          value: formatPercent(summary.sameDayTreatmentRate.current, 0),
          valueOver: goalForMetric(mockSecondaryMetrics, 'same-day-tx'),
          showWarningIcon:
            Math.round(summary.sameDayTreatmentRate.current) <
            Math.round(
              parsePercentGoal(
                goalForMetric(mockSecondaryMetrics, 'same-day-tx'),
              ),
            ),
        }
      case 'pending-tx-value':
        return {
          ...metric,
          value: formatCurrency(pendingValue, { compact: true }),
        }
      case 'new-patients':
        return {
          ...metric,
          value: newPatientCount.toLocaleString('en-US'),
          showWarningIcon:
            Math.round(newPatientCount) <
            Math.round(
              parsePercentGoal(
                goalForMetric(mockSecondaryMetrics, 'new-patients'),
              ),
            ),
        }
      default:
        return metric
    }
  })
}

/** Maps detail chart open-chair hours series to Overview line-chart shape. */
export function mapSameDayCancellationChartToOverviewLineChart(
  chart: SameDayCancellationChartConfig | null | undefined,
): OverviewSameDayLineChartConfig | null {
  if (!chart?.data?.length) {
    return null
  }
  const data = chart.data.map((point) => ({
    label: point.label,
    value: point.openChairHours ?? 0,
  }))
  const maxValue = data.reduce((max, point) => Math.max(max, point.value), 0)
  const yMax = Math.max(Math.ceil(maxValue), 1)
  const yTicks: number[] = []
  for (let tick = 0; tick <= yMax; tick += 1) {
    yTicks.push(tick)
  }
  return {
    data,
    yDomain: [0, yMax],
    yTicks,
  }
}

export function mapSchedulingOverviewToSameDayCancellationMetrics(
  overview: SchedulingOverviewResult,
  mockMetrics: DashboardMetricItemData[],
): DashboardMetricItemData[] {
  const { summary } = overview
  const byId = new Map(mockMetrics.map((m) => [m.id, m]))
  return [
    {
      ...(byId.get('sdc-total') ?? {
        id: 'sdc-total',
        leftLabel: 'Total appointments',
      }),
      value: Math.round(
        summary.sameDayCancellationTotalAppointments.current,
      ).toLocaleString('en-US'),
    },
    {
      ...(byId.get('sdc-cancelled') ?? {
        id: 'sdc-cancelled',
        leftLabel: 'Same-day cancellation',
      }),
      value: Math.round(summary.sameDayCancellations.current).toLocaleString(
        'en-US',
      ),
    },
    {
      ...(byId.get('sdc-open-chair') ?? {
        id: 'sdc-open-chair',
        leftLabel: 'Open chair time',
      }),
      value: formatDurationMinutes(
        summary.sameDayCancellationOpenChairMinutes.current,
      ),
    },
  ]
}

export function mapSchedulingOverviewToAppointmentOverrunMetrics(
  overview: SchedulingOverviewResult,
  mockMetrics: DashboardMetricItemData[],
): DashboardMetricItemData[] {
  const { summary } = overview
  const byId = new Map(mockMetrics.map((m) => [m.id, m]))
  return [
    {
      ...(byId.get('overrun-appts') ?? {
        id: 'overrun-appts',
        leftLabel: 'Overrun appointments',
      }),
      value: Math.round(summary.appointmentOverruns.current).toLocaleString(
        'en-US',
      ),
    },
    {
      ...(byId.get('overrun-completed') ?? {
        id: 'overrun-completed',
        leftLabel: 'Completed appointments',
      }),
      value: Math.round(
        summary.appointmentOverrunCompleted.current,
      ).toLocaleString('en-US'),
    },
    {
      ...(byId.get('overrun-avg-delay') ?? {
        id: 'overrun-avg-delay',
        leftLabel: 'Avg. delay',
      }),
      value: formatDurationMinutes(
        summary.appointmentOverrunAvgDelayMinutes.current,
      ),
    },
  ]
}
